// Tasks feature API. Three endpoint groups, one feature:
//   TasksBank/*             — the bank itself (lookup lists, search, CRUD, Excel)
//   TasksBank/CourseTasks/* — Stage 1: linking a bank task to a course
//   StructuredPlan/*        — Stage 2: placing a linked task into a plan
// Split out of AdminService — nothing outside the tasks views/dialogs calls these.
import axios from "axios";

const BASE_URL = process.env.VUE_APP_APIURL;

// ===== Bank lookups =====

const getTaskTypes = () => axios.get(`${BASE_URL}/api/Admin/TasksBank/TaskTypes`);

const getTaskSubTypes = (taskTypeId) =>
  axios.get(`${BASE_URL}/api/Admin/TasksBank/TaskTypes/${taskTypeId}/SubTypes`);

const getTaskCategories = () => axios.get(`${BASE_URL}/api/Admin/TasksBank/Categories`);

// ===== The bank =====

// Paged bank-task search. Body (TasksBankQuery, all optional):
//   Terms, CourseId, TaskTypeId, SubTypeId, SubjectId, Status,
//   SortedBy (taskName/duration/id), IsAscending, PageNumber, PageSize.
// Response: PagingResults<TasksBankModel> -> { items, totalResults, totalPages, currentPage }.
const getTasksInBank = (data, cancelToken) =>
  axios.post(`${BASE_URL}/api/Admin/TasksBank/Tasks/Search`, data, { cancelToken });

const createTask = (data) => axios.post(`${BASE_URL}/api/Admin/TasksBank/Tasks`, data);

const updateTask = (taskId, data) => axios.put(`${BASE_URL}/api/Admin/TasksBank/Tasks/${taskId}`, data);

const setTasksStatus = (taskIds, active) =>
  axios.put(`${BASE_URL}/api/Admin/TasksBank/SetStatus/${active}`, taskIds);

// ===== Excel round trip for the bank =====
// Export/Format return .xlsx blobs; the filename comes back in Content-Disposition.
// Import never auto-links tasks to a course, so it takes no courseId.
// Export covers ACTIVE tasks only; retired ones are never written to the sheet.
// courseId/programTypeId are optional filters: axios drops null/undefined params,
// so leaving one out sends no key at all rather than the string "null".
const exportTasksBank = (courseId = null, programTypeId = null) =>
  axios.get(`${BASE_URL}/api/Admin/TasksBank/Export`, {
    params: { courseId, programTypeId },
    responseType: 'blob',
  });

// Blank template: sheet "תבנית" (44 headers, required marked *) + "עזרה - סוגי משימות".
const getTasksBankFormat = () =>
  axios.get(`${BASE_URL}/api/Admin/TasksBank/Format`, { responseType: 'blob' });

// All-or-nothing: any bad row rejects the WHOLE file and nothing is written.
// Rows key off task_id — empty creates, an existing id is a FULL REPLACE (blank
// cells clear fields), an unknown id fails the file. There is no is_active column,
// so an import can never activate or retire a task.
// 200 -> { Message, Created, Updated }; 400 -> { Error, Errors? } (Errors may be absent).
const importTasksBank = (formData) =>
  axios.post(`${BASE_URL}/api/Admin/TasksBank/Import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// ===== Stage 1 — course membership =====

// Link a bank task to a course (idempotent). Returns the CourseTaskModel membership.
// body: { TasksBankId, CourseId }
const linkTaskToCourse = (data) =>
  axios.post(`${BASE_URL}/api/Admin/TasksBank/CourseTasks`, data);

// A course's task memberships (each with its nested bank task), program-type independent.
// body (all optional except paging): { Terms, Status, PageSize, PageNumber, SortedBy, IsAscending }
// Response: PagingResults<CourseTaskModel> -> { items: [{ tasksBankCourseId, courseId, task }], totalResults }.
const getCourseTaskMemberships = (courseId, data, cancelToken) =>
  axios.post(`${BASE_URL}/api/Admin/TasksBank/CourseTasks/${courseId}/Search`, data, { cancelToken });

// Unlink a bank task from a course. Cascades: removes the task from every plan placement
// in that course (DB ON DELETE CASCADE). Returns { Message } (404 if not linked).
const unlinkTaskFromCourse = (tasksBankId, courseId) =>
  axios.delete(`${BASE_URL}/api/Admin/TasksBank/CourseTasks`, { params: { tasksBankId, courseId } });

// ===== Stage 2 — the structured plan =====

// Program types (plans) available for a course. Returns [{ id, name }].
const getProgramTypes = () => axios.get(`${BASE_URL}/api/Admin/StructuredPlan/ProgramTypes`);

// Flat AdvancedSearch for the course's placed tasks. Body (all optional except scope):
//   CourseId, ProgramTypeId (scope), PageSize, PageNumber, SortedBy, IsAscending,
//   Terms, FromDate, ToDate, TimeWindow, Status.
// Response: PagingResults wrapper -> { items, totalResults, ... }.
// Duration comes back as FOUR fields per row — a placement may pin its own duration for
// this program instead of inheriting the shared bank's:
//   durationMinutes          — the RESOLVED value (override if set, bank otherwise). Display
//                              this, and sum this for day totals. It is no longer the raw bank value.
//   durationMinutesOverride  — the saved override; null = this placement inherits the bank.
//                              Editable inputs bind to THIS, not to durationMinutes.
//   isDurationOverridden     — true = pinned on this program (bank edits no longer reach it)
//   bankDurationMinutes      — what inheriting gives; use as the input's placeholder.
const getCourseTasks = (data, cancelToken) =>
  axios.post(`${BASE_URL}/api/Admin/StructuredPlan/Search`, data, { cancelToken });

// The admin's note for ONE plan day, keyed the same way the plan is: (CourseId, ProgramTypeId) + date.
// Response: { comments } — null when no note was written.
const getPlanDayComments = (courseId, programTypeId, taskDate) =>
  axios.get(`${BASE_URL}/api/Admin/StructuredPlan/PlanDays/Comments`, {
    params: { courseId, programTypeId, taskDate },
  });

// Set/clear the day note. Body: { CourseId, ProgramTypeId, TaskDate, Comments }.
// A blank Comments clears the day. Returns { Message }.
const setPlanDayComments = (data) =>
  axios.put(`${BASE_URL}/api/Admin/StructuredPlan/PlanDays/Comments`, data);

// Place one or more memberships into a plan (batch). Body -> AddStructuredPlanTasksModel:
//   { CourseId, ProgramTypeId, Tasks: [{ TasksBankCourseId, TaskDate, StartTime?, EndTime?,
//                                        TimeWindow?, DurationMinutes? }] }
// SortOrder is auto-assigned per day; tasks already placed in this plan are skipped server-side.
// DurationMinutes is the per-program override — omit it (or send null) to inherit the bank's.
// It must be > 0 when sent; 0/negative rejects the WHOLE batch (400 -> { Error }).
const addStructuredPlanTask = (data) =>
  axios.post(`${BASE_URL}/api/Admin/StructuredPlan/PlanTasks`, data);

// Update placed plan tasks (batch). Takes one row or an array; each row carries its own
// id, so rows may span days and program types. Body -> UpdateStructuredPlanTasksModel:
//   { Tasks: [{ Id, TaskDate, SortOrder, StartTime?, EndTime?, TimeWindow?, DurationMinutes? }] }
// Id/TaskDate/SortOrder are required per row and NOT preserved server-side — a missing
// SortOrder deserializes to 0 and moves the row, so pass the row's current value when
// you only mean to change schedule fields.
// The same applies to DurationMinutes, where null is MEANINGFUL: it CLEARS the per-program
// override and puts the row back to inheriting the bank. So whenever you edit a row for any
// other reason (moving it, changing its time window), echo back its current
// durationMinutesOverride — omitting the field wipes the override. Never echo durationMinutes
// (the resolved value): that would silently pin the bank's duration onto every row you touch.
// Must be > 0 when sent; 0/negative rejects the whole batch.
// All-or-nothing: a bad row rejects the whole batch (400 -> { Error }) and nothing is
// written. 200 -> the updated StructuredPlanTaskModel[], not a count.
const updateStructuredPlanTasks = (rows) =>
  axios.put(`${BASE_URL}/api/Admin/StructuredPlan/PlanTasks`, {
    Tasks: Array.isArray(rows) ? rows : [rows],
  });

// Move one task up (-1) or down (1) within its day. Server handles the per-day logic.
const moveStructuredPlanTask = (planTaskId, direction) =>
  axios.put(`${BASE_URL}/api/Admin/StructuredPlan/PlanTasks/${planTaskId}/Move`, { direction });

// Reorder a single day's tasks. Body: [{ id, sortOrder }, ...] (one day only).
// The numbers are just intended order; the server normalizes to clean 1..N.
const reorderStructuredPlanTasks = (rows) =>
  axios.put(`${BASE_URL}/api/Admin/StructuredPlan/PlanTasks/Reorder`, rows);

// Remove placed plan tasks. Takes one id or an array; ids may span days and program
// types (a placement is identified by its id alone). Body: { ids: [...] } — DELETE
// carries a body, so it goes under axios's `data` option.
// Best-effort per id: unknown ids are skipped, not rejected, so the response's
// deletedCount may be lower than the requested count. `message` is ready to toast as-is.
const deleteStructuredPlanTask = (planTaskIds) =>
  axios.delete(`${BASE_URL}/api/Admin/StructuredPlan/PlanTasks`, {
    data: { ids: Array.isArray(planTaskIds) ? planTaskIds : [planTaskIds] },
  });

export default {
  getTaskTypes,
  getTaskSubTypes,
  getTaskCategories,
  getTasksInBank,
  createTask,
  updateTask,
  setTasksStatus,
  exportTasksBank,
  getTasksBankFormat,
  importTasksBank,
  linkTaskToCourse,
  getCourseTaskMemberships,
  unlinkTaskFromCourse,
  getProgramTypes,
  getCourseTasks,
  getPlanDayComments,
  setPlanDayComments,
  addStructuredPlanTask,
  updateStructuredPlanTasks,
  moveStructuredPlanTask,
  reorderStructuredPlanTasks,
  deleteStructuredPlanTask
};
